// @ts-nocheck
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { ChartData, ChartOptions } from 'chart.js';
import * as XLSX from 'xlsx';
import type { ColDef, GridApi, GridReadyEvent, CellValueChangedEvent } from 'ag-grid-community';
import {
  NJButton,
  NJCheckbox,
  NJFormItem,
  NJIcon,
  NJIconButton,
  NJInlineMessage,
  NJSegmentedControl,
  NJSegmentedControlButton,
  NJSpinner,
  NJTab,
  NJTabPanel,
  NJTabs,
  NJText,
  NJToggle,
  NJToastContainer,
  NJToastService,
  NJTooltip,
} from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { MixedChart, FileUploadCard } from '@shared';
import DataTable, { getCellClassName } from '@shared/DataTable';
import { useDemandPreview, usePlannings, useStorages } from './hooks';
import type {
  AdvancedOptions,
  DemandHourlyRow,
  DemandTab,
  FoisonnementKind,
  IceStorageRow,
  NetworkStorageRow,
  RegulationMode,
  TabId,
  VizMode,
} from './types';

export const meta = {
  title: "Formulaire de création d'une optimisation courte terme",
  description:
    "Formulaire multi-onglets pour configurer une optimisation courte terme : général, températures et demande (avec grille éditable, undo/redo, import/export), stockages, planning, options avancées.",
  tags: ['form', 'form, short-term, optimization, wizard'],
  author: 'pm-team',
  status: 'ready' as const,
};

// ─── Constants ────────────────────────────────────────────────────────────────

const DELTA_TEMPERATURE = 'delta_t';
const OUTPUT_TEMPERATURE = 'output_temperature';

const TABS: { id: TabId; label: string; shortLabel: string }[] = [
  { id: 'general',             label: '1. Général',                 shortLabel: '1' },
  { id: 'temperatures-demand', label: '2. Températures et demande', shortLabel: '2' },
  { id: 'storages',            label: '3. Stockages',               shortLabel: '3' },
  { id: 'planning',            label: '4. Planning',                shortLabel: '4' },
  { id: 'other',               label: '5. Autre',                   shortLabel: '5' },
  { id: 'advanced',            label: '6. Options avancées',        shortLabel: '6' },
];

function useIsSmallScreen() {
  const [isSmall, setIsSmall] = useState(() => window.matchMedia('(max-width: 900px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const handler = (e: MediaQueryListEvent) => setIsSmall(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isSmall;
}

const DEFAULT_ADVANCED_OPTIONS: AdvancedOptions = {
  simplified_pressure_loss_model: false,
  total_production_correction: false,
  delta_t_min: false,
  ets_flow_correction: false,
  multiple_prod_temperatures: false,
  handle_power_gaps: false,
  gf_switch_penalty: false,
  rolling_anticipative: false,
  unit_commitment_short_term: false,
  aggregate_lt_timesteps: false,
  cold_water_aggregation_level: false,
  aggregate_non_detailed_timesteps: false,
  use_generic_tar_model: false,
  endogenous_recirculation: false,
  variable_exchanger_delta_p: false,
  bypass_activation: false,
  convex_hull_pumps: true,
  evaporator_pumps: false,
  condenser_pumps: false,
  generate_gf_detailed_files: false,
  generate_limiting_factors_report: true,
  short: false,
  cop_optimization: false,
  flow_replay_prod_sites: false,
  short_term_constraints_target: false,
  test_feasopt: false,
  relax_regulators: false,
  writelp: false,
  debug_generate_logs: false,
  data_check: false,
  iis: false,
  debug_target_gdx: false,
  limit_resolution_time: false,
};

const SP24 = 'var(--nj-semantic-size-spacing-24)';
const SP16 = 'var(--nj-semantic-size-spacing-16)';
const SP8  = 'var(--nj-semantic-size-spacing-8)';

// ─── Date / step utilities (mirrors optimisations.ts in the real app) ─────────

function computeEndDate(startDate: string, nbSteps: number): string {
  const d = new Date(startDate);
  d.setHours(d.getHours() + nbSteps);
  return d.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm for datetime-local
}

function computeNbSteps(startDate: string, endDate: string): number {
  return Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / 3_600_000);
}

function formatWithTimezone(timestamp: number, tz: string): { isoDate: string; utcOffset: string } {
  const date = new Date(timestamp);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  const isoDate = `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`;

  const offsetPart = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' })
    .formatToParts(date).find(p => p.type === 'timeZoneName')?.value;
  const match = offsetPart?.match(/GMT([+-])(\d+)(?::(\d+))?/);
  const utcOffset = match ? `${match[1]}${match[2].padStart(2, '0')}:${match[3] ?? '00'}` : '+00:00';
  return { isoDate, utcOffset };
}

function generateDemandRows(startDate: string, nbSteps: number): DemandHourlyRow[] {
  return Array.from({ length: nbSteps }, (_, i) => {
    const d = new Date(startDate);
    d.setHours(d.getHours() + i);
    const pad = (n: number) => String(n).padStart(2, '0');
    const label = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return {
      id: String(i),
      date: label,
      dryTemperature: null,
      wetTemperature: null,
      coldWaterTemperature: null,
      totalProduction: null,
    };
  });
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

interface ToggleRowProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}
function ToggleRow({ label, checked, onChange }: ToggleRowProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SP8, paddingBlock: '6px' }}>
      <NJToggle label="" checked={checked} onChange={() => onChange(!checked)} />
      <NJText scale="sm" style={{ margin: 0 }}>{label}</NJText>
    </div>
  );
}

interface ToggleSectionProps { title: string; children: React.ReactNode; }
function ToggleSection({ title, children }: ToggleSectionProps) {
  return (
    <div style={{ marginBottom: SP24 }}>
      <p style={{
        margin: `0 0 ${SP8} 0`,
        fontSize: '16px',
        fontWeight: 'var(--nj-core-font-weight-semibold)',
        textTransform: 'capitalize',
      }}>
        {title}
      </p>
      {children}
    </div>
  );
}


// ─── Tab 1: Général ───────────────────────────────────────────────────────────

interface GeneralTabProps {
  nom: string; onNomChange: (v: string) => void;
  description: string; onDescriptionChange: (v: string) => void;
  startDate: string; onStartDateChange: (v: string) => void;
  nombreDePas: string; onNombreDePasChange: (v: string) => void;
  endDate: string; onEndDateChange: (v: string) => void;
  regulationMode: RegulationMode; onRegulationModeChange: (v: RegulationMode) => void;
  advancedOptions: AdvancedOptions;
  onToggleOption: (key: keyof AdvancedOptions, value: boolean) => void;
}
function GeneralTab({
  nom, onNomChange,
  description, onDescriptionChange,
  startDate, onStartDateChange,
  nombreDePas, onNombreDePasChange,
  endDate, onEndDateChange,
  regulationMode, onRegulationModeChange,
  advancedOptions, onToggleOption,
}: GeneralTabProps) {
  const isSmall = useIsSmallScreen();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      minHeight: '600px',
      overflowY: 'auto',
      background: '#ffffff',
    }}>

      {/* ── Left column: Description ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: SP24 }}>
        <NJText style={{ fontFamily: "'Lato', sans-serif", display: 'block', marginBottom: SP16 }}>
          <strong>Optimisation Court Terme</strong>
        </NJText>
        <div style={{ textAlign: 'justify', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <NJText scale="sm">
            {"L'"}
            <strong style={{ color: '#0070AD' }}>optimisation courte terme</strong>
            {" est un calcul visant à minimiser les coûts opérationnels (OPEX) sur un horizon limité (généralement une semaine, ou un mois)."}
          </NJText>
          <NJText scale="sm">
            Elle cherche à satisfaire la demande en eau chaude ou froide sur des périodes courtes,
            en mobilisant tous les moyens de production disponibles, en tenant compte de leur
            efficacité et des coûts des énergies.
          </NJText>
          <NJText scale="sm">
            Elle modélise les écoulements et les caractéristiques hydrauliques du réseau (débits,
            pressions, températures) qui sont critiques pour l&apos;exploitation opérationnelle.
            Elle intègre la topologie complète du réseau (parties isolées, vannes fermées, substations)
            pour garantir la faisabilité physique des solutions.
          </NJText>
          <NJText scale="sm">
            Elle prend en compte les contraintes opérationnelles court terme comme les marges de
            manœuvre des unités de production, les temps de démarrage et arrêt, ainsi que les
            contraintes issues de la trajectoire long terme (cibles de consommation énergétique,
            mix énergétique) pour assurer la cohérence avec les objectifs à long terme.
          </NJText>
        </div>
      </div>

      {/* ── Separator ── */}
      <div style={{ width: '1px', background: 'var(--nj-core-color-grey-300)', margin: `0 ${SP16}` }} />

      {/* ── Middle column: Informations générales ── */}
      <div style={{ flex: 1.8, padding: SP24 }}>
        <NJText style={{ fontFamily: "'Lato', sans-serif", display: 'block' }}>
          <strong>Informations générales</strong>
        </NJText>

        <div style={{ marginTop: SP16 }}>
          <NJFormItem
            labelKind="static"
            value={nom}
            id="label"
            label={isSmall ? 'Nom *' : 'Nom (Obligatoire)'}
            onChange={(e) => onNomChange((e.target as HTMLInputElement).value)}
          />
        </div>

        <div style={{ marginTop: SP16 }}>
          <NJFormItem
            labelKind="static"
            value={description}
            id="description"
            label="Description"
            isMultiline
            maxLength={200}
            onChange={(e) => onDescriptionChange((e.target as HTMLInputElement).value)}
          />
        </div>

        <div style={{ marginTop: SP16 }}>
          <NJFormItem
            value={startDate}
            id="start_date"
            label={isSmall ? 'Start date *' : 'Start date (Obligatoire)'}
            type="datetime-local"
            onChange={(e) => onStartDateChange((e.target as HTMLInputElement).value)}
          />
        </div>

        <div style={{ marginTop: SP16 }}>
          <NJFormItem
            labelKind="static"
            value={nombreDePas}
            id="nombre_de_pas"
            label="Nombre de pas"
            type="number"
            min={1}
            step={1}
            onChange={(e) => onNombreDePasChange((e.target as HTMLInputElement).value)}
          />
        </div>

        <div style={{ marginTop: SP16 }}>
          <NJFormItem
            value={endDate}
            id="end_date"
            label={isSmall ? 'End date *' : 'End date (Obligatoire)'}
            type="datetime-local"
            onChange={(e) => onEndDateChange((e.target as HTMLInputElement).value)}
          />
        </div>

        <div style={{ marginTop: SP16, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="material-icons" style={{ fontSize: '22px', color: 'var(--nj-semantic-color-brand-primary, #0070AD)' }}>schedule</span>
          <NJText scale="sm" style={{ margin: 0, fontWeight: 600, fontFamily: "'Lato', sans-serif" }}>
            Timezone: Europe/Paris
          </NJText>
        </div>

        <div style={{ marginTop: SP16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: SP8 }}>
            <NJText style={{ display: 'block', margin: 0, fontFamily: "'Lato', sans-serif", fontWeight: 500, color: '#334551' }}>
              Mode de régulation des sous-stations
            </NJText>
            <NJTooltip
              text={
                <div style={{ fontSize: '13px', lineHeight: '1.5', fontFamily: "'Lato', sans-serif", color: '#FFFFFF', maxWidth: '240px', textAlign: 'left' }}>
                  <strong>Delta T</strong> — régulation basée sur l&apos;écart de température entre départ et retour.<br /><br />
                  <strong>T Retour</strong> — régulation basée sur la température de retour imposée en sous-station.
                </div>
              }
              hasArrow
              placement="top"
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', width: '16px', height: '16px' }}>
                <NJIcon name="info_outlined" scale="sm" />
              </span>
            </NJTooltip>
          </div>
          <NJSegmentedControl
            label=""
            onChange={(v: string) => onRegulationModeChange(v as RegulationMode)}
            value={regulationMode}
            size="lg"
          >
            <NJSegmentedControlButton label="Delta T" value={DELTA_TEMPERATURE} />
            <NJSegmentedControlButton label="T Retour" value={OUTPUT_TEMPERATURE} />
          </NJSegmentedControl>
        </div>
      </div>

      {/* ── Separator ── */}
      <div style={{ width: '1px', background: 'var(--nj-core-color-grey-300)', margin: `0 ${SP16}` }} />

      {/* ── Right column: Options d'étude ── */}
      <div style={{ flex: 1, padding: SP24 }}>
        <NJText style={{ fontFamily: "'Lato', sans-serif", display: 'block' }}>
          <strong>Options d'étude</strong>
        </NJText>
        <div style={{ marginTop: SP16 }}>
          <ToggleRow
            label="Dimensionnement des canalisations"
            checked={true}
            onChange={() => {}}
          />
          <ToggleRow
            label="Activation de la réserve de puissance"
            checked={false}
            onChange={() => {}}
          />
          <ToggleRow
            label="Activation du chemin critique"
            checked={false}
            onChange={() => {}}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Reset confirmation modal ─────────────────────────────────────────────────

function ResetConfirmationModal({ isOpen, onClose, onConfirm }: { isOpen: boolean; onClose: () => void; onConfirm: () => void }) {
  if (!isOpen) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#FFFFFF',
        borderRadius: '0px',
        padding: '24px',
        width: '560px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        position: 'relative',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          style={{
            position: 'absolute', top: '16px', right: '16px',
            background: 'transparent', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
          }}
        >
          <span className="material-icons" style={{ fontSize: '22px', color: '#171D21' }}>close</span>
        </button>

        {/* Centered body */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', paddingTop: '24px', paddingBottom: '8px' }}>
          <span
            className="material-icons-outlined"
            aria-hidden="true"
            style={{ fontSize: '64px', color: 'var(--nj-semantic-color-feedback-danger-icon, #d3373c)', flexShrink: 0, flexGrow: 0 }}
          >
            info
          </span>

          <h2 style={{
            fontFamily: "'Lato', sans-serif", fontStyle: 'normal', fontWeight: 700,
            fontSize: '24px', lineHeight: '32px', textAlign: 'center',
            color: '#171D21', margin: 0, alignSelf: 'stretch', flexGrow: 0,
          }}>
            Confirmation de reinitialisation
          </h2>

          <p style={{
            fontFamily: "'Lato', sans-serif", fontStyle: 'normal', fontWeight: 400,
            fontSize: '16px', lineHeight: '24px', textAlign: 'center',
            color: '#171D21', margin: 0, alignSelf: 'stretch', flexGrow: 0,
          }}>
            Êtes-vous bien certain de vouloir vider le tableau ?<br />
            Toutes les données seront perdues.
          </p>
        </div>

        {/* Buttons */}
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '24px' }}>
          <NJButton
            label="Annuler"
            variant="secondary"
            emphasis="subtle"
            scale="md"
            onClick={onClose}
            style={{ minWidth: '140px' }}
          />
          <NJButton
            label="Je reinitialise"
            variant="primary"
            emphasis="bold"
            scale="md"
            onClick={onConfirm}
            style={{ minWidth: '140px' }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Tab 2: Températures et demande ──────────────────────────────────────────

interface TempDemandTabProps {
  onProductionValidityChange?: (isValid: boolean) => void;
  startDate: string;
  nbSteps: number;
}

function TempDemandTab({ onProductionValidityChange, startDate, nbSteps }: TempDemandTabProps) {
  const startDateRef = useRef(startDate);
  startDateRef.current = startDate;
  const [demandTab, setDemandTab]             = useState<DemandTab>('consommation');
  const [vizMode, setVizMode]                 = useState<VizMode>('tableau');
  const [kbdOpen, setKbdOpen]                 = useState(false);
  const [consumptionType, setConsumptionType] = useState<'profils' | 'file'>('profils');
  const [consumptionFileName, setConsumptionFileName] = useState<string | null>(null);
  const [consumptionUploadStatus, setConsumptionUploadStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [coeffFoisonnement, setCoeffFoisonnement] = useState<string>('1');
  const [foisonnementType, setFoisonnementType] = useState<FoisonnementKind>('consumption');

  const layoutRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen]       = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      layoutRef.current?.requestFullscreen();
    }
  }, []);

  const chartRoRef = useRef<ResizeObserver | null>(null);
  const [chartHeight, setChartHeight]         = useState(400);
  const chartContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (chartRoRef.current) { chartRoRef.current.disconnect(); chartRoRef.current = null; }
    if (!node) return;
    chartRoRef.current = new ResizeObserver(([entry]) => setChartHeight(entry.contentRect.height));
    chartRoRef.current.observe(node);
  }, []);

  const isProductionTab     = demandTab === 'production';

  // ── Editable rows ──────────────────────────────────────────────────────────
  const { data: preview }   = useDemandPreview();
  const gridApiRef          = useRef<GridApi | null>(null);
  const [localRows, setLocalRows] = useState<DemandHourlyRow[]>([]);
  const localRowsRef        = useRef<DemandHourlyRow[]>([]);
  localRowsRef.current      = localRows;
  const isProductionTabRef  = useRef(isProductionTab);
  isProductionTabRef.current = isProductionTab;

  // Generate rows from startDate + nbSteps (mirrors useOptimisationDataTableRows in the app)
  useEffect(() => {
    if (!startDate || nbSteps <= 0) {
      localRowsRef.current = [];
      setLocalRows([]);
      return;
    }
    const rows = generateDemandRows(startDate, nbSteps);
    localRowsRef.current = rows;
    setLocalRows(rows);
    undoStack.current = [];
    redoStack.current = [];
    setHasHistory(false);
    setHasFuture(false);
  }, [startDate, nbSteps]);

  useEffect(() => {
    if (!onProductionValidityChange) return;
    const isValid = !isProductionTab || localRows.every(r => r.totalProduction !== null);
    onProductionValidityChange(isValid);
  }, [isProductionTab, localRows, onProductionValidityChange]);

  const undoStack   = useRef<DemandHourlyRow[][]>([]);
  const redoStack   = useRef<DemandHourlyRow[][]>([]);
  const [hasHistory, setHasHistory] = useState(false);
  const [hasFuture, setHasFuture]   = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [uploadFileName, setUploadFileName] = useState<string | null>(null);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isTableHovered, setIsTableHovered] = useState(false);

  const [selectedColField, setSelectedColField] = useState<string | null>(null);
  const selectedColFieldRef = useRef<string | null>(null);
  selectedColFieldRef.current = selectedColField;
  const [selectedRows, setSelectedRows]   = useState<Set<number>>(new Set());
  const selectedRowsRef = useRef<Set<number>>(new Set());
  selectedRowsRef.current = selectedRows;
  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api;
  }, []);

  const applyRowsToGrid = useCallback((rows: DemandHourlyRow[]) => {
    const api = gridApiRef.current;
    if (!api) return;
    api.forEachNode((node, i) => {
      if (rows[i] !== undefined) node.setData({ ...rows[i] });
    });
  }, []);

  const handleCellValueChanged = useCallback((event: CellValueChangedEvent) => {
    if (event.oldValue === event.newValue) return;
    const field    = event.colDef.field!;
    const rowIndex = event.rowIndex!;
    undoStack.current.push([...localRowsRef.current]);
    redoStack.current = [];
    const newVal = event.newValue === '' || event.newValue === null ? null : Number(event.newValue);
    const next   = localRowsRef.current.map((r, i) =>
      i === rowIndex ? { ...r, [field]: newVal } : r
    );
    localRowsRef.current = next;
    setLocalRows(next);
    setHasHistory(true);
    setHasFuture(false);
  }, []);

  // Enter → commit + move to next row
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (!document.activeElement?.closest('.ag-cell-editor')) return;
      const focused = gridApiRef.current?.getFocusedCell();
      if (!focused) return;
      e.preventDefault();
      e.stopPropagation();
      const nextRow = focused.rowIndex + 1;
      gridApiRef.current?.stopEditing(false);
      if (nextRow < localRowsRef.current.length) {
        setTimeout(() => {
          gridApiRef.current?.setFocusedCell(nextRow, focused.column.getColId());
        }, 0);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const handleUndo = useCallback(() => {
    const prev = undoStack.current.pop();
    if (!prev) return;
    redoStack.current.unshift([...localRowsRef.current]);
    localRowsRef.current = prev;
    setLocalRows([...prev]);
    applyRowsToGrid(prev);
    setHasHistory(undoStack.current.length > 0);
    setHasFuture(true);
  }, [applyRowsToGrid]);

  const handleRedo = useCallback(() => {
    const next = redoStack.current.shift();
    if (!next) return;
    undoStack.current.push([...localRowsRef.current]);
    localRowsRef.current = next;
    setLocalRows([...next]);
    applyRowsToGrid(next);
    setHasHistory(true);
    setHasFuture(redoStack.current.length > 0);
  }, [applyRowsToGrid]);

  const handleReset = useCallback(() => {
    const rows = (startDate && nbSteps > 0)
      ? generateDemandRows(startDate, nbSteps)
      : [];
    localRowsRef.current = rows;
    setLocalRows([...rows]);
    applyRowsToGrid(rows);
    undoStack.current = [];
    redoStack.current = [];
    setHasHistory(false);
    setHasFuture(false);
    setSelectedColField(null);
    setSelectedRows(new Set());
    selectedRowsRef.current = new Set();
    setUploadStatus('idle');
    setUploadFileName(null);
    setImportError(null);
  }, [startDate, nbSteps, applyRowsToGrid]);

  const copyColumnToClipboard = useCallback(async (field: string) => {
    const tsv = localRowsRef.current
      .map(r => String(r[field as keyof DemandHourlyRow] ?? ''))
      .join('\n');
    await navigator.clipboard.writeText(tsv);
    NJToastService('La colonne a été copiée avec succès', {
      title: '', iconName: 'content_copy', hasCloseIcon: true,
    });
  }, []);

  const copyCellToClipboard = useCallback(async (rowIndex: number, field: string) => {
    const row = localRowsRef.current[rowIndex];
    if (!row) return;
    const value = row[field as keyof DemandHourlyRow];
    await navigator.clipboard.writeText(value === null || value === undefined ? '' : String(value));
    NJToastService('La cellule a été copiée', { title: '', iconName: 'content_copy', hasCloseIcon: true });
  }, []);

  const copyRowsToClipboard = useCallback(async (rowsToCopy: DemandHourlyRow[]) => {
    const prod    = isProductionTabRef.current;
    const headers = prod
      ? ['Date', 'Température sèche (°C)', 'Température humide (°C)', 'Température eau refroid. (°C)', 'Production totale (MW)']
      : ['Date', 'Température sèche (°C)', 'Température humide (°C)', 'Température eau refroid. (°C)'];
    const tsv = [
      headers.join('\t'),
      ...rowsToCopy.map(r =>
        prod
          ? [r.date, r.dryTemperature, r.wetTemperature, r.coldWaterTemperature, r.totalProduction].join('\t')
          : [r.date, r.dryTemperature, r.wetTemperature, r.coldWaterTemperature].join('\t')
      ),
    ].join('\n');
    await navigator.clipboard.writeText(tsv);
    const n    = rowsToCopy.length;
    const total = localRowsRef.current.length;
    const msg  = n === total
      ? 'Le tableau a été copié avec succès'
      : n === 1 ? 'La ligne a été copiée avec succès' : `Les ${n} lignes ont été copiées avec succès`;
    NJToastService(msg, { title: '', iconName: 'content_copy', hasCloseIcon: true });
  }, []);

  const downloadRowsAsXLSX = useCallback(() => {
    const prod     = isProductionTabRef.current;
    const base     = startDateRef.current;
    const tz       = 'Europe/Paris';
    const headers  = prod
      ? ['Date', 'utc_offset', 'Température sèche (°C)', 'Température humide (°C)', 'Température eau refroidissement (°C)', 'Production totale (MW)']
      : ['Date', 'utc_offset', 'Température sèche (°C)', 'Température humide (°C)', 'Température eau refroidissement (°C)'];
    const sheetData = [
      headers,
      ...localRowsRef.current.map((r, i) => {
        let isoDate = r.date;
        let utcOffset = '+00:00';
        if (base) {
          const ts = new Date(base).getTime() + i * 3_600_000;
          ({ isoDate, utcOffset } = formatWithTimezone(ts, tz));
        }
        return prod
          ? [isoDate, utcOffset, r.dryTemperature ?? '', r.wetTemperature ?? '', r.coldWaterTemperature ?? '', r.totalProduction ?? '']
          : [isoDate, utcOffset, r.dryTemperature ?? '', r.wetTemperature ?? '', r.coldWaterTemperature ?? ''];
      }),
    ];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Données');
    XLSX.writeFile(wb, prod
      ? 'Template_trajectoire_temperatures_production.xlsx'
      : 'Template_trajectoire_temperatures.xlsx');
  }, []);

  const importFileRef      = useRef<HTMLInputElement | null>(null);
  const loadingTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);

  const importFile = useCallback((file: File) => {
    const COL = {
      date:                 'Date',
      dryTemperature:       'Température sèche (°C)',
      wetTemperature:       'Température humide (°C)',
      coldWaterTemperature: 'Température eau refroidissement (°C)',
      totalProduction:      'Production totale (MW)',
    };
    setUploadFileName(file.name);
    setImportError(null);
    if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    loadingTimerRef.current = setTimeout(() => setUploadStatus('loading'), 300);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const buffer = evt.target?.result;
      if (!buffer) return;
      const wb      = XLSX.read(buffer, { type: 'array' });
      const ws      = wb.Sheets[wb.SheetNames[0]];
      const rawRows = XLSX.utils.sheet_to_json<(string | number | null)[]>(ws, { header: 1 });
      if (!rawRows.length) {
        if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
        setUploadStatus('idle');
        NJToastService('Le fichier est vide.', { title: 'Erreur d\'import', iconName: 'error', hasCloseIcon: true });
        return;
      }
      const headerRow = rawRows[0].map(h => String(h ?? '').trim().toLowerCase());
      const findCol   = (name: string) => headerRow.indexOf(name.toLowerCase());
      const idxDate   = findCol(COL.date);
      const idxDry    = findCol(COL.dryTemperature);
      const idxWet    = findCol(COL.wetTemperature);
      const idxCold   = findCol(COL.coldWaterTemperature);
      const idxProd   = findCol(COL.totalProduction);
      const missing: string[] = [];
      if (idxDry  === -1) missing.push(COL.dryTemperature);
      if (idxWet  === -1) missing.push(COL.wetTemperature);
      if (idxCold === -1) missing.push(COL.coldWaterTemperature);
      if (missing.length > 0) {
        if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
        setUploadStatus('idle');
        NJToastService(`Colonne(s) introuvable(s) : ${missing.join(', ')}`, { title: 'Erreur d\'import', iconName: 'error', hasCloseIcon: true });
        return;
      }
      const dataRows = rawRows.slice(1);
      const prod     = isProductionTabRef.current;
      const newRows  = localRowsRef.current.map((r, i) => {
        if (i >= dataRows.length) return r;
        const cols    = dataRows[i];
        const updated: DemandHourlyRow = { ...r };
        if (idxDate >= 0 && cols[idxDate] !== undefined && cols[idxDate] !== null) {
          updated.date = String(cols[idxDate]);
        }
        const vDry = cols[idxDry];
        if (vDry !== undefined && vDry !== null && vDry !== '') {
          const v = Number(vDry);
          if (Number.isFinite(v)) updated.dryTemperature = v;
        }
        const vWet = cols[idxWet];
        if (vWet !== undefined && vWet !== null && vWet !== '') {
          const v = Number(vWet);
          if (Number.isFinite(v)) updated.wetTemperature = v;
        }
        const vCold = cols[idxCold];
        if (vCold !== undefined && vCold !== null && vCold !== '') {
          const v = Number(vCold);
          if (Number.isFinite(v)) updated.coldWaterTemperature = v;
        }
        if (prod && idxProd >= 0) {
          const vProd = cols[idxProd];
          if (vProd !== undefined && vProd !== null && vProd !== '') {
            const v = Number(vProd);
            if (Number.isFinite(v)) updated.totalProduction = v;
          } else {
            updated.totalProduction = null;
          }
        }
        return updated;
      });
      undoStack.current.push([...localRowsRef.current]);
      redoStack.current = [];
      localRowsRef.current = newRows;
      setLocalRows([...newRows]);
      applyRowsToGrid(newRows);
      setHasHistory(true);
      setHasFuture(false);
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
      setUploadStatus('success');
    };
    reader.readAsArrayBuffer(file);
  }, [applyRowsToGrid]);

  const handleImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    importFile(file);
    e.target.value = '';
  }, [importFile]);

  const handleDragOver  = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
  }, []);
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      NJToastService('Format non supporté — veuillez déposer un fichier Excel (.xlsx)', { title: '', iconName: 'error', hasCloseIcon: true });
      return;
    }
    importFile(file);
  }, [importFile]);

  const handleGridContainerClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as Element;
    const headerCell = target.closest('.ag-header-cell[col-id]');
    if (headerCell) {
      const colId = headerCell.getAttribute('col-id');
      if (!colId) return;
      selectedRowsRef.current = new Set();
      setSelectedRows(new Set());
      if (colId === 'date') {
        setSelectedColField(prev => prev === '__all__' ? null : '__all__');
      } else {
        setSelectedColField(prev => prev === colId ? null : colId);
      }
      return;
    }
    const dateCell = target.closest('.ag-cell[col-id="date"]');
    if (dateCell) {
      const rowEl = dateCell.closest('.ag-row');
      if (!rowEl) return;
      const rowIdx = parseInt(rowEl.getAttribute('row-index') ?? '-1', 10);
      if (rowIdx < 0) return;
      selectedColFieldRef.current = null;
      setSelectedColField(null);
      const already = selectedRowsRef.current.has(rowIdx);
      const next    = already ? new Set<number>() : new Set([rowIdx]);
      selectedRowsRef.current = next;
      setSelectedRows(next);
      gridApiRef.current?.refreshCells({ force: true });
      return;
    }
    if (target.closest('.ag-cell')) {
      selectedColFieldRef.current = null;
      setSelectedColField(null);
      selectedRowsRef.current = new Set();
      setSelectedRows(new Set());
      gridApiRef.current?.refreshCells({ force: true });
    }
  }, []);

  useEffect(() => {
    const container = gridContainerRef.current;
    if (!container) return;
    container.querySelectorAll<HTMLElement>('.ag-header-cell[col-id]').forEach((el) => {
      el.style.background = '';
    });
    if (selectedColField === '__all__') {
      container.querySelectorAll<HTMLElement>('.ag-header-cell[col-id]').forEach((el) => {
        if (el.getAttribute('col-id') !== 'date') el.style.background = '#DBEAFE';
      });
    } else if (selectedColField) {
      const el = container.querySelector<HTMLElement>(`.ag-header-cell[col-id="${selectedColField}"]`);
      if (el) el.style.background = '#DBEAFE';
    }
    gridApiRef.current?.refreshCells({ force: true });
  }, [selectedColField]);

  const pasteFromClipboard = useCallback(async () => {
    try {
      const text     = await navigator.clipboard.readText();
      const lines    = text.trim().split('\n').filter(Boolean);
      if (lines.length === 0) return;
      const startLine = isNaN(Number(lines[0].split('\t')[0])) ? 1 : 0;
      const dataLines = lines.slice(startLine);
      if (dataLines.length === 0) return;
      const prod      = isProductionTabRef.current;
      const targetCol = selectedColFieldRef.current;

      if (targetCol && targetCol !== '__all__' && dataLines.length === 1 && !dataLines[0].includes('\t')) {
        const val = Number(dataLines[0]);
        if (!isNaN(val)) {
          const newRows = localRowsRef.current.map(r => ({ ...r, [targetCol]: val }));
          undoStack.current.push([...localRowsRef.current]);
          redoStack.current = [];
          localRowsRef.current = newRows;
          setLocalRows([...newRows]);
          applyRowsToGrid(newRows);
          setHasHistory(true);
          setHasFuture(false);
          NJToastService(`Colonne remplie avec ${val}`, { title: '', iconName: 'content_copy', hasCloseIcon: true });
          return;
        }
      }

      const newRows = localRowsRef.current.map((r, i) => {
        if (i >= dataLines.length) return r;
        const cols = dataLines[i].split('\t');
        if (targetCol) {
          const val = Number(cols[0]);
          return isNaN(val) ? r : { ...r, [targetCol]: val };
        }
        const hasDateCol = prod ? cols.length >= 5 : cols.length >= 4;
        const off     = hasDateCol ? 1 : 0;
        const dry     = Number(cols[off]);
        const wet     = Number(cols[off + 1]);
        const cold    = Number(cols[off + 2]);
        const prod_v  = Number(cols[off + 3]);
        return {
          ...r,
          ...(isNaN(dry)    ? {} : { dryTemperature: dry }),
          ...(isNaN(wet)    ? {} : { wetTemperature: wet }),
          ...(isNaN(cold)   ? {} : { coldWaterTemperature: cold }),
          ...(prod && !isNaN(prod_v) ? { totalProduction: prod_v } : {}),
        };
      });
      undoStack.current.push([...localRowsRef.current]);
      redoStack.current = [];
      localRowsRef.current = newRows;
      setLocalRows([...newRows]);
      applyRowsToGrid(newRows);
      setHasHistory(true);
      setHasFuture(false);
    } catch { /* clipboard access denied */ }
  }, [applyRowsToGrid]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag     = (document.activeElement?.tagName ?? '').toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea';
      const key     = e.key.toLowerCase();

      if (key === 'delete' || key === 'backspace') {
        const col = selectedColFieldRef.current;
        if (!col || isTyping) return;
        e.preventDefault();
        undoStack.current.push([...localRowsRef.current]);
        redoStack.current = [];
        const next = col === '__all__'
          ? localRowsRef.current.map(r => ({ ...r, dryTemperature: null, wetTemperature: null, coldWaterTemperature: null, totalProduction: null }))
          : localRowsRef.current.map(r => ({ ...r, [col]: null }));
        localRowsRef.current = next;
        setLocalRows([...next]);
        applyRowsToGrid(next);
        setHasHistory(true);
        setHasFuture(false);
        return;
      }

      const ctrl = e.metaKey || e.ctrlKey;
      if (!ctrl) return;
      if (key === 'z' && !e.shiftKey) {
        if (isTyping) return;
        e.preventDefault();
        handleUndo();
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        if (isTyping) return;
        e.preventDefault();
        handleRedo();
      } else if (key === 'c') {
        const isInCellEditor = isTyping && !!document.activeElement?.closest('.ag-cell');
        if (isTyping && !isInCellEditor) return;
        if (isInCellEditor) {
          NJToastService('La cellule a été copiée', { title: '', iconName: 'content_copy', hasCloseIcon: true });
          return;
        }
        const col = selectedColFieldRef.current;
        if (col === '__all__') {
          void copyRowsToClipboard(localRowsRef.current);
        } else if (col) {
          void copyColumnToClipboard(col);
        } else if (selectedRowsRef.current.size > 0) {
          const rows = localRowsRef.current.filter((_, i) => selectedRowsRef.current.has(i));
          void copyRowsToClipboard(rows);
        } else {
          const focused = gridApiRef.current?.getFocusedCell();
          if (focused && focused.column.getColId() !== 'date') {
            void copyCellToClipboard(focused.rowIndex, focused.column.getColId());
          }
        }
      } else if (key === 'a') {
        if (isTyping) return;
        e.preventDefault();
        selectedRowsRef.current = new Set();
        setSelectedRows(new Set());
        setSelectedColField('__all__');
      } else if (key === 'v') {
        if (isTyping) return;
        e.preventDefault();
        void pasteFromClipboard();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleUndo, handleRedo, copyRowsToClipboard, copyColumnToClipboard, copyCellToClipboard, pasteFromClipboard, applyRowsToGrid]);

  // ── Chart ──────────────────────────────────────────────────────────────────
  const chartData = useMemo<ChartData<'bar' | 'line'>>(
    () => ({
      labels: localRows.map(r => r.date),
      datasets: [
        ...(isProductionTab ? [{
          type: 'bar' as const,
          label: 'Production totale (MW)',
          data: localRows.map(r => r.totalProduction),
          backgroundColor: 'rgba(63, 81, 181, 0.8)',
          borderColor: '#3f51b5',
          borderWidth: 1,
          borderRadius: 2,
          pointStyle: 'rect' as const,
          yAxisID: 'yProd',
          order: 2,
        }] : []),
        {
          type: 'line' as const,
          label: 'Température sèche (°C)',
          data: localRows.map(r => r.dryTemperature),
          borderColor: '#E87A3A',
          backgroundColor: '#ffffff',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.3,
          fill: false,
          yAxisID: 'yTemp',
          order: 1,
        },
        {
          type: 'line' as const,
          label: 'Température humide (°C)',
          data: localRows.map(r => r.wetTemperature),
          borderColor: '#E8B432',
          backgroundColor: '#ffffff',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.3,
          fill: false,
          yAxisID: 'yTemp',
          order: 1,
        },
        {
          type: 'line' as const,
          label: 'Température de refroidissement (°C)',
          data: localRows.map(r => r.coldWaterTemperature),
          borderColor: '#C87DC8',
          backgroundColor: '#ffffff',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.3,
          fill: false,
          yAxisID: 'yTemp',
          order: 1,
        },
      ],
    }),
    [localRows, isProductionTab],
  );

  const chartOptions = useMemo<ChartOptions<'bar' | 'line'>>(
    () => ({
      responsive: true,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom', labels: { usePointStyle: true, padding: 16 } },
        tooltip: {
          usePointStyle: true,
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              if (v === null || v === undefined) return '';
              return `${ctx.dataset.label ?? ''} : ${v.toFixed(2)}`;
            },
          },
        },
      },
      scales: {
        x: {
          title: { display: true, text: 'Date (Europe/Paris)', font: { size: 12 }, color: '#212121' },
          grid: { color: '#f0f0f0' },
          ticks: { maxTicksLimit: 14, maxRotation: 45 },
        },
        yTemp: {
          type: 'linear',
          position: 'left',
          title: { display: true, text: 'Température (°C)', font: { size: 12 }, color: '#212121' },
          grid: { color: '#f0f0f0' },
        },
        ...(isProductionTab ? {
          yProd: {
            type: 'linear',
            position: 'right',
            beginAtZero: true,
            title: { display: true, text: 'Production (MW)', font: { size: 12 }, color: '#212121' },
            ticks: { color: '#616161' },
            grid: { drawOnChartArea: false },
          },
        } : {}),
      },
    }),
    [isProductionTab],
  );

  // ── Column defs ────────────────────────────────────────────────────────────
  const colDefs = useMemo<ColDef<DemandHourlyRow>[]>(() => [
    {
      field: 'date',
      headerName: 'Date [Europe/Paris]',
      width: 170,
      maxWidth: 200,
      editable: false,
      sortable: false,
      filter: false,
      cellClass: getCellClassName({ readOnly: true }),
      cellStyle: { textAlign: 'left', backgroundColor: '#F0F0F0', color: '#8C9AA6' },
      headerClass: 'ag-header-cell-center index-col-header',
    },
    {
      field: 'dryTemperature',
      headerName: 'Température\nsèche (°C)',
      flex: 1,
      editable: true,
      filter: 'agTextColumnFilter',
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: getCellClassName({ readOnly: false }),
      cellStyle: (params: { rowIndex: number | null }) => {
        const rowSel = params.rowIndex !== null && selectedRowsRef.current.has(params.rowIndex);
        const colSel = selectedColFieldRef.current === 'dryTemperature' || selectedColFieldRef.current === '__all__';
        return { textAlign: 'center' as const, ...(rowSel ? { backgroundColor: '#F0FDF4' } : colSel ? { backgroundColor: '#DBEAFE' } : {}) };
      },
      headerClass: 'ag-header-cell-center',
      valueFormatter: ({ value }: { value: number | null }) =>
        value === null || value === undefined ? '-' : Number(value).toFixed(2),
    },
    {
      field: 'wetTemperature',
      headerName: 'Température\nhumide (°C)',
      flex: 1,
      editable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: getCellClassName({ readOnly: false }),
      cellStyle: (params: { rowIndex: number | null }) => {
        const rowSel = params.rowIndex !== null && selectedRowsRef.current.has(params.rowIndex);
        const colSel = selectedColFieldRef.current === 'wetTemperature' || selectedColFieldRef.current === '__all__';
        return { textAlign: 'center' as const, ...(rowSel ? { backgroundColor: '#F0FDF4' } : colSel ? { backgroundColor: '#DBEAFE' } : {}) };
      },
      headerClass: 'ag-header-cell-center',
      valueFormatter: ({ value }: { value: number | null }) =>
        value === null || value === undefined ? '-' : Number(value).toFixed(2),
    },
    {
      field: 'coldWaterTemperature',
      headerName: 'Température de\nrefroidissement (°C)',
      flex: 1,
      editable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: getCellClassName({ readOnly: false }),
      cellStyle: (params: { rowIndex: number | null }) => {
        const rowSel = params.rowIndex !== null && selectedRowsRef.current.has(params.rowIndex);
        const colSel = selectedColFieldRef.current === 'coldWaterTemperature' || selectedColFieldRef.current === '__all__';
        return { textAlign: 'center' as const, ...(rowSel ? { backgroundColor: '#F0FDF4' } : colSel ? { backgroundColor: '#DBEAFE' } : {}) };
      },
      headerClass: 'ag-header-cell-center',
      valueFormatter: ({ value }: { value: number | null }) =>
        value === null || value === undefined ? '-' : Number(value).toFixed(2),
    },
    ...(isProductionTab ? [{
      field: 'totalProduction' as const,
      headerName: 'Production\ntotale (MW)',
      flex: 1,
      editable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: getCellClassName({ readOnly: false }),
      cellStyle: (params: { rowIndex: number | null }) => {
        const rowSel = params.rowIndex !== null && selectedRowsRef.current.has(params.rowIndex);
        const colSel = selectedColFieldRef.current === 'totalProduction' || selectedColFieldRef.current === '__all__';
        return { textAlign: 'center' as const, ...(rowSel ? { backgroundColor: '#F0FDF4' } : colSel ? { backgroundColor: '#DBEAFE' } : {}) };
      },
      headerClass: 'ag-header-cell-center',
      valueFormatter: ({ value }: { value: number | null }) =>
        value === null || value === undefined ? '-' : Number(value).toFixed(2),
    }] : []),
  ], [isProductionTab]);

  return (
    <div style={{ display: 'flex', minHeight: '600px', height: '100%' }}>

      {/* ── Left: Paramétrage des données d'entrées ── */}
      <div style={{
        width: '410px',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRight: '1px solid var(--nj-semantic-color-border-secondary-default)',
      }}>
        <div style={{ padding: `${SP24} ${SP24} 0 ${SP24}`, marginBottom: SP16 }}>
          <NJText style={{ fontWeight: 700, margin: 0, fontFamily: "'Lato', sans-serif" }}>
            Paramétrage des données d&apos;entrées
          </NJText>
        </div>

        <div style={{ padding: `0 ${SP24} ${SP24} ${SP24}`, overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: 0 }}>

          {/* Définition de la demande */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: SP8 }}>
            <NJText scale="sm" style={{ fontWeight: 400, margin: 0, fontFamily: "'Lato', sans-serif", color: 'var(--nj-semantic-color-text-primary-default)' }}>
              Définition de la demande
            </NJText>
            <NJTooltip
              text={
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', lineHeight: '1.5', fontFamily: "'Lato', sans-serif", color: '#FFFFFF', textAlign: 'left' }}>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: '4px' }}>Consommation</div>
                    <div>Fixer la consommation de chaque point de livraison.</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: '4px' }}>Production</div>
                    <div>Fixer la puissance totale injectée sur le réseau. Celle-ci sera répartie sur chaque point de livraison en fonction de leur consommation.</div>
                  </div>
                </div>
              }
              hasArrow
              placement="top"
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', width: '16px', height: '16px' }}>
                <NJIcon name="info_outlined" scale="sm" />
              </span>
            </NJTooltip>
          </div>

          {/* Consommation / Production toggle */}
          <div style={{ marginBottom: SP16 }}>
            <NJSegmentedControl
              label=""
              value={demandTab}
              onChange={(v) => setDemandTab(v as DemandTab)}
              size="lg"
            >
              <NJSegmentedControlButton label="Consommation" value="consommation" />
              <NJSegmentedControlButton label="Production" value="production" />
            </NJSegmentedControl>
          </div>

          {/* ── Températures card ── */}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
            padding: '16px', gap: '12px', background: '#F6F8F9', marginBottom: SP16, boxSizing: 'border-box',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="material-icons" style={{ fontSize: '24px', color: '#171D21' }}>device_thermostat</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <NJText style={{ fontWeight: 700, margin: 0, fontFamily: "'Lato', sans-serif" }}>
                  {isProductionTab ? 'Températures et production' : 'Températures'}
                </NJText>
                <NJTooltip
                  text={
                    <div style={{ fontSize: '14px', lineHeight: '1.5', fontFamily: "'Lato', sans-serif", color: '#FFFFFF' }}>
                      {isProductionTab
                        ? 'Fixer les valeurs horaires de températures sèche, humide, de refroidissement et production totale prises en compte par l\'optimisation.'
                        : 'Fixer les valeurs horaires de températures sèche, humide et de refroidissement prises en compte par l\'optimisation.'}
                    </div>
                  }
                  hasArrow
                  placement="right"
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', width: '16px', height: '16px' }}>
                    <NJIcon name="info_outlined" scale="sm" />
                  </span>
                </NJTooltip>
              </div>
            </div>
            <NJText scale="sm" style={{ color: 'var(--nj-semantic-color-text-tertiary-default, #9BABB7)', fontFamily: "'Lato', sans-serif", margin: 0 }}>
              {isProductionTab
                ? 'Températures sèches, humides, de refroidissement et production totale (horaires)'
                : 'Températures sèches, humides et de refroidissement (horaires)'}
            </NJText>
            <input
              ref={importFileRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleImport}
            />
            <NJButton
              label="Choisir un fichier"
              variant="primary"
              scale="md"
              icon="link"
              onClick={() => importFileRef.current?.click()}
              style={{ width: '200px', height: '40px', flexShrink: 0, flexGrow: 0 }}
            />
          </div>

          {/* ── Consommation card (only in consommation tab) ── */}
          {(
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
              padding: '16px', gap: '12px', background: '#F6F8F9',
              alignSelf: 'stretch', boxSizing: 'border-box',
            }}>
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                padding: 0, gap: '12px', alignSelf: 'stretch',
              }}>
                {/* Header: house icon + title */}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="12" cy="12" r="12" fill="white"/>
                    <g clipPath="url(#clip0_conso)">
                      <path d="M12 5.69L17 10.19V18H15V12H9V18H7V10.19L12 5.69ZM12 3L2 12H5V20H11V14H13V20H19V12H22L12 3Z" fill="#171D21"/>
                    </g>
                    <defs><clipPath id="clip0_conso"><rect width="24" height="24" fill="white"/></clipPath></defs>
                  </svg>
                  <NJText style={{ fontWeight: 700, margin: 0, fontFamily: "'Lato', sans-serif" }}>Consommation</NJText>
                </div>

                {/* Type de consommation */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: SP8 }}>
                    <NJText scale="sm" style={{ fontWeight: 400, margin: 0, fontFamily: "'Lato', sans-serif", color: 'var(--nj-semantic-color-text-primary-default)' }}>
                      Type de consommation
                    </NJText>
                    <NJTooltip
                      text={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', lineHeight: '1.5', fontFamily: "'Lato', sans-serif", color: '#FFFFFF', textAlign: 'left' }}>
                          <div>
                            <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '14px' }}>Profils</div>
                            <div style={{ fontWeight: 400, fontSize: '14px' }}>Calculer les consommations des points de livraison à partir des profils définis sur les points de livraison en fonction de la température extérieure.</div>
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '14px' }}>Fichier</div>
                            <div style={{ fontWeight: 400, fontSize: '14px' }}>Fixer la consommation par heure et par client via un fichier de consommations.</div>
                          </div>
                        </div>
                      }
                      hasArrow
                      placement="right"
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', width: '16px', height: '16px' }}>
                        <NJIcon name="info_outlined" scale="sm" />
                      </span>
                    </NJTooltip>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="radio"
                      id="conso-profils"
                      name="trajectory-consommation-type"
                      checked={consumptionType === 'profils'}
                      onChange={() => { setConsumptionType('profils'); setConsumptionFileName(null); setConsumptionUploadStatus('idle'); }}
                      style={{ accentColor: '#0052CC', width: '16px', height: '16px', margin: 0, cursor: 'pointer', flexShrink: 0 }}
                    />
                    <label htmlFor="conso-profils" style={{ fontSize: '14px', fontFamily: "'Lato', sans-serif", color: '#171D21', lineHeight: '20px', cursor: 'pointer', marginRight: '12px' }}>
                      Profils simples
                    </label>
                    <input
                      type="radio"
                      id="conso-fichier"
                      name="trajectory-consommation-type"
                      checked={consumptionType === 'file'}
                      onChange={() => setConsumptionType('file')}
                      style={{ accentColor: '#0052CC', width: '16px', height: '16px', margin: 0, cursor: 'pointer', flexShrink: 0 }}
                    />
                    <label htmlFor="conso-fichier" style={{ fontSize: '14px', fontFamily: "'Lato', sans-serif", color: '#171D21', lineHeight: '20px', cursor: 'pointer' }}>
                      Fichier
                    </label>
                  </div>
                </div>

                {/* CSV upload — only when "fichier" selected */}
                {consumptionType === 'file' && (
                  <>
                    <input
                      type="file"
                      id="trajectory-consumption-file-input"
                      accept=".csv"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setConsumptionFileName(file.name);
                        setConsumptionUploadStatus('loading');
                        setTimeout(() => setConsumptionUploadStatus('success'), 800);
                        e.target.value = '';
                      }}
                    />
                    <NJButton
                      label="Choisir un fichier"
                      variant="primary"
                      emphasis="bold"
                      scale="md"
                      icon="link"
                      onClick={() => document.getElementById('trajectory-consumption-file-input')?.click()}
                    />
                    {consumptionUploadStatus === 'loading' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '18px', height: '18px', borderRadius: '50%',
                          border: '2px solid #e0e0e0', borderTopColor: '#0070AD',
                          animation: 'nj-spin 0.8s linear infinite',
                        }} />
                        <NJText scale="sm" style={{ color: 'var(--nj-semantic-color-text-secondary-default)', margin: 0 }}>Import en cours…</NJText>
                      </div>
                    )}
                    {consumptionUploadStatus === 'success' && consumptionFileName && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '6px 10px', border: '1px solid #90CAF9',
                        background: '#E7EEFC', boxSizing: 'border-box', width: '100%',
                      }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: '20px', height: '20px', borderRadius: '50%', background: '#2E7D32', flexShrink: 0,
                        }}>
                          <span className="material-icons" style={{ fontSize: '14px', color: '#fff' }}>check</span>
                        </span>
                        <NJText scale="sm" style={{ flex: 1, minWidth: 0, wordBreak: 'break-word', margin: 0 }}>{consumptionFileName}</NJText>
                        <button
                          type="button"
                          onClick={() => { setConsumptionUploadStatus('idle'); setConsumptionFileName(null); }}
                          style={{ flexShrink: 0, cursor: 'pointer', background: 'transparent', border: 'none', padding: 0 }}
                        >
                          <span className="material-icons" style={{ fontSize: '18px', color: 'var(--nj-semantic-color-text-secondary-default)' }}>delete</span>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Type de foisonnement */}
              <div>
                <NJText scale="sm" style={{ fontWeight: 400, margin: 0, fontFamily: "'Lato', sans-serif", marginBottom: '8px', display: 'block', color: 'var(--nj-semantic-color-text-primary-default)' }}>
                  Type de foisonnement
                </NJText>
                <NJSegmentedControl
                  label=""
                  value={foisonnementType}
                  onChange={(v) => setFoisonnementType(v as FoisonnementKind)}
                  size="lg"
                >
                  <NJSegmentedControlButton label="Automatique" value="automatic" />
                  <NJSegmentedControlButton label="Canalisation" value="channeling" />
                  <NJSegmentedControlButton label="Consommation" value="consumption" />
                </NJSegmentedControl>
              </div>

              {/* Coefficient de foisonnement */}
              <div style={{ width: '100%' }}>
                <NJText scale="sm" style={{ fontWeight: 400, margin: 0, fontFamily: "'Lato', sans-serif", marginBottom: '8px', display: 'block', color: 'var(--nj-semantic-color-text-primary-default)' }}>
                  Coefficient de foisonnement (valeur entre 0 et 1)
                </NJText>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input
                    id="coeff-foisonnement"
                    type="number"
                    value={isProductionTab || foisonnementType !== 'consumption' ? '1' : coeffFoisonnement}
                    disabled={isProductionTab || foisonnementType !== 'consumption'}
                    onChange={(e) => {
                      if (!isProductionTab && foisonnementType === 'consumption') setCoeffFoisonnement(e.target.value);
                    }}
                    style={{
                      width: '100%',
                      height: '40px',
                      border: (() => {
                        const isCoeffDisabled = isProductionTab || foisonnementType !== 'consumption';
                        const v = parseFloat(coeffFoisonnement);
                        const invalid = !isCoeffDisabled && coeffFoisonnement !== '' && !isNaN(v) && (v < 0 || v > 1);
                        return invalid ? '1px solid #F34E46' : '1px solid #CCD4DA';
                      })(),
                      borderRadius: '0px',
                      padding: '8px 36px 8px 12px',
                      fontSize: '14px',
                      fontFamily: "'Lato', sans-serif",
                      color: '#171D21',
                      background: isProductionTab || foisonnementType !== 'consumption' ? '#F6F8F9' : '#ffffff',
                      boxSizing: 'border-box',
                      outline: 'none',
                    }}
                  />
                  <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'auto' }}>
                    <NJTooltip
                      text={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', lineHeight: '1.5', fontFamily: "'Lato', sans-serif", color: '#FFFFFF', textAlign: 'left' }}>
                          <div style={{ fontWeight: 400, fontSize: '14px' }}>Ce coefficient (0 à 1) ajuste la consommation de chaque point de livraison pour simuler des scénarios avec foisonnement.</div>
                          <div>
                            <div style={{ fontWeight: 700, marginBottom: '8px', fontSize: '14px' }}>Exemples :</div>
                            <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '14px', fontWeight: 400 }}>
                              <li>1 = inchangé</li>
                              <li>0.8 = réduction de 20 %</li>
                              <li>0.5 = réduction de 50 %</li>
                            </ul>
                          </div>
                        </div>
                      }
                      hasArrow
                      placement="right"
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', width: '16px', height: '16px' }}>
                        <NJIcon name="info_outlined" scale="sm" />
                      </span>
                    </NJTooltip>
                  </div>
                </div>
                {(() => {
                  const v = parseFloat(coeffFoisonnement);
                  const invalid = !isProductionTab && foisonnementType === 'consumption' && coeffFoisonnement !== '' && !isNaN(v) && (v < 0 || v > 1);
                  if (!invalid) return null;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                      <span className="material-icons" style={{ fontSize: '16px', color: '#F34E46', flexShrink: 0 }}>warning</span>
                      <NJText scale="sm" style={{ color: '#F34E46' }}>
                        La valeur doit être comprise entre 0 et 1.
                      </NJText>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Right: Visualisation des données d'entrée ── */}
      <div ref={layoutRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: SP24, minWidth: 0 }}>
        <NJToastContainer />
        <ResetConfirmationModal
          isOpen={isResetModalOpen}
          onClose={() => setIsResetModalOpen(false)}
          onConfirm={() => { setIsResetModalOpen(false); handleReset(); }}
        />

        {/* Header row */}
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: SP16, gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <NJText style={{ fontWeight: 700, margin: 0, fontFamily: "'Lato', sans-serif" }}>
              Visualisation des données d&apos;entrée
            </NJText>
            {localRows.length > 0 && (
              <span style={{
                fontSize: '12px', color: 'var(--nj-semantic-color-text-secondary-default)',
                background: '#F6F8F9', padding: '2px 8px', borderRadius: '12px',
                border: '1px solid #CCD4DA', fontFamily: "'Lato', sans-serif",
              }}>
                {localRows.length} lignes
              </span>
            )}
            <span
              style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
              onMouseEnter={() => setKbdOpen(true)}
              onMouseLeave={() => setKbdOpen(false)}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                <span className="material-icons" style={{ color: '#171d21', fontSize: '20px', lineHeight: 1 }}>keyboard</span>
              </span>
              {kbdOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 8px)', left: '50%',
                  transform: 'translateX(-50%)', zIndex: 999,
                  background: '#212121', borderRadius: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
                  padding: '14px 16px', width: '360px', fontFamily: "'Lato', sans-serif", pointerEvents: 'none',
                }}>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: '#fff', marginBottom: '10px' }}>
                    Raccourcis clavier
                  </div>
                  {([
                    { keys: ['Clic'],               desc: 'sur en-tête — Sélectionner une colonne' },
                    { keys: ['⌘A', 'Ctrl+A'],       desc: '— Sélectionner tout le tableau' },
                    { keys: ['⌘C', 'Ctrl+C'],       desc: '— Copier la sélection' },
                    { keys: ['⌘V', 'Ctrl+V'],       desc: '— Coller dans la colonne sélectionnée' },
                    { keys: ['Delete', 'Backspace'], desc: '— Vider la colonne sélectionnée' },
                    { keys: ['⌘Z', 'Ctrl+Z'],       desc: '— Annuler la dernière action' },
                    { keys: ['⌘Y', 'Ctrl+Y'],       desc: "— Rétablir l'action annulée" },
                    { keys: ['Double-clic'],         desc: 'sur cellule — Éditer' },
                    { keys: ['Enter'],               desc: '— Valider et passer à la ligne suivante' },
                    { keys: ['Tab'],                 desc: '— Passer à la cellule suivante' },
                    { keys: ['Shift+Tab'],           desc: '— Revenir à la cellule précédente' },
                    { keys: ['Échap'],               desc: '— Annuler la saisie' },
                  ] as { keys: string[]; desc: string }[]).map(({ keys, desc }) => (
                    <div key={keys[0]} style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '7px', fontSize: '12px', color: '#e0e0e0', flexWrap: 'wrap' }}>
                      {keys.map((k, i) => (
                        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 7px', fontSize: '11px', fontFamily: 'monospace',
                            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.35)',
                            borderRadius: '4px', color: '#fff', lineHeight: '18px', whiteSpace: 'nowrap',
                          }}>{k}</span>
                          {i < keys.length - 1 && <span style={{ color: '#9e9e9e' }}>/</span>}
                        </span>
                      ))}
                      <span>{desc}</span>
                    </div>
                  ))}
                </div>
              )}
            </span>

            {/* Toolbar */}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '2px' }}>
              <NJTooltip text="Télécharger le fichier modèle" placement="top" hasArrow>
                <NJIconButton icon="download" label="Télécharger le fichier modèle" variant="primary" scale="xs" onClick={downloadRowsAsXLSX} isDisabled={localRows.length === 0} />
              </NJTooltip>
              <NJTooltip text="Copier le tableau" placement="top" hasArrow>
                <NJIconButton icon="content_copy" label="Copier le tableau" variant="primary" scale="xs" onClick={() => void copyRowsToClipboard(localRows)} isDisabled={localRows.length === 0} />
              </NJTooltip>
              <NJTooltip text="Réinitialiser le tableau" placement="top" hasArrow>
                <NJIconButton icon="cached" label="Réinitialiser le tableau" variant="primary" scale="xs" onClick={() => setIsResetModalOpen(true)} isDisabled={localRows.length === 0} />
              </NJTooltip>
              <NJTooltip text="Annuler la dernière modification" placement="top" hasArrow>
                <NJIconButton icon="undo" label="Annuler la dernière modification" variant="primary" scale="xs" onClick={handleUndo} isDisabled={!hasHistory} />
              </NJTooltip>
              <NJTooltip text="Rétablir la dernière modification" placement="top" hasArrow>
                <NJIconButton icon="redo" label="Rétablir la dernière modification" variant="primary" scale="xs" onClick={handleRedo} isDisabled={!hasFuture} />
              </NJTooltip>
              <div style={{ width: '1px', height: '20px', background: 'var(--nj-semantic-color-border-secondary-default)', margin: '0 6px' }} />
              <NJSegmentedControl
                label="Mode de visualisation"
                value={vizMode}
                onChange={(v) => {
                  if (v === 'graphique') setSelectedColField(null);
                  setVizMode(v as VizMode);
                }}
                size="sm"
              >
                <NJSegmentedControlButton label="Tableau" value="tableau" iconName="table_chart" />
                <NJSegmentedControlButton label="Graphique" value="graphique" iconName="bar_chart" />
              </NJSegmentedControl>
              <NJTooltip text={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'} placement="top" hasArrow>
                <NJIconButton icon={isFullscreen ? 'fullscreen_exit' : 'fullscreen'} label={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'} variant="primary" scale="xs" onClick={toggleFullscreen} />
              </NJTooltip>
            </div>
          </div>

          {/* Warning badges */}
          {(() => {
            const filledDry  = localRows.filter(r => r.dryTemperature !== null).length;
            const filledWet  = localRows.filter(r => r.wetTemperature !== null).length;
            const filledCold = localRows.filter(r => r.coldWaterTemperature !== null).length;
            const filledProd = localRows.filter(r => r.totalProduction !== null).length;
            const dryInc  = localRows.length > 0 && filledDry  < localRows.length;
            const wetInc  = localRows.length > 0 && filledWet  < localRows.length;
            const coldInc = localRows.length > 0 && filledCold < localRows.length;
            const prodInc = isProductionTab && localRows.length > 0 && filledProd < localRows.length;
            if (!dryInc && !wetInc && !coldInc && !prodInc) return null;
            const wStyle: CSSProperties = {
              fontSize: '12px', color: '#B45309', background: '#FFFBEB',
              padding: '2px 8px', borderRadius: '12px', border: '1px solid #FCD34D',
              fontFamily: "'Lato', sans-serif", display: 'inline-flex', alignItems: 'center', gap: '4px',
            };
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                {dryInc  && <span style={wStyle}><span className="material-icons" style={{ fontSize: '13px' }}>warning_amber</span>{filledDry}/{localRows.length} température sèche renseignée</span>}
                {wetInc  && <span style={wStyle}><span className="material-icons" style={{ fontSize: '13px' }}>warning_amber</span>{filledWet}/{localRows.length} température humide renseignée</span>}
                {coldInc && <span style={wStyle}><span className="material-icons" style={{ fontSize: '13px' }}>warning_amber</span>{filledCold}/{localRows.length} température de refroidissement renseignée</span>}
                {prodInc && <span style={wStyle}><span className="material-icons" style={{ fontSize: '13px' }}>warning_amber</span>{filledProd}/{localRows.length} production renseignée</span>}
              </div>
            );
          })()}
        </div>

        {/* Content: table or chart */}
        {vizMode === 'tableau' ? (
          <div
            ref={gridContainerRef}
            onClick={handleGridContainerClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onMouseEnter={() => setIsTableHovered(true)}
            onMouseLeave={() => setIsTableHovered(false)}
            style={{
              flex: 1, minHeight: 0,
              background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
              borderRadius: 'var(--nj-core-border-radius-200)',
              padding: '12px', overflow: 'hidden',
              display: 'flex', flexDirection: 'column', position: 'relative',
            }}
          >
            {isDragOver && localRows.length > 0 && (
              <div style={{
                position: 'absolute', inset: 0, zIndex: 20,
                background: 'rgba(59,130,246,0.08)', border: '2px dashed #3B82F6',
                borderRadius: 'var(--nj-core-border-radius-200)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '8px', pointerEvents: 'none',
              }}>
                <span className="material-icons" style={{ fontSize: '40px', color: '#3B82F6' }}>upload_file</span>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#3B82F6' }}>Déposez votre fichier Excel ici</span>
              </div>
            )}

            {uploadStatus === 'loading' && (
              <div style={{
                position: 'absolute', inset: 0, zIndex: 21,
                background: 'rgba(0,0,0,0.25)', borderRadius: 'var(--nj-core-border-radius-200)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{
                  background: '#fff', padding: '40px 48px',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px',
                  minWidth: '280px', position: 'relative',
                }}>
                  <button
                    type="button"
                    onClick={() => setUploadStatus('idle')}
                    style={{
                      position: 'absolute', top: '12px', right: '12px',
                      width: '36px', height: '36px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: '1px solid #333', background: '#fff', cursor: 'pointer', padding: 0,
                    }}
                  >
                    <span className="material-icons" style={{ fontSize: '20px' }}>close</span>
                  </button>
                  <div style={{
                    width: '64px', height: '64px', borderRadius: '50%',
                    border: '4px solid var(--nj-core-color-grey-200, #e0e0e0)',
                    borderTopColor: 'var(--nj-semantic-color-brand-primary, #0078D4)',
                    animation: 'nj-spin 0.8s linear infinite',
                  }} />
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: '18px', marginBottom: '8px', fontFamily: "'Lato', sans-serif" }}>Veuillez patienter…</div>
                    <div style={{ fontSize: '14px', color: '#666', fontFamily: "'Lato', sans-serif" }}>L'import du fichier est en cours</div>
                  </div>
                </div>
              </div>
            )}

            <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {/* Empty state: no rows generated yet (no start date / steps) */}
              {localRows.length === 0 && (
                <div style={{
                  position: 'absolute', inset: 0, zIndex: 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  pointerEvents: 'none',
                }}>
                  <span style={{ fontFamily: "'Lato', sans-serif", fontSize: '14px', color: 'var(--nj-semantic-color-text-tertiary-default, #9BABB7)' }}>
                    Aucune donnée à afficher
                  </span>
                </div>
              )}

              {/* Drop overlay: rows exist but no data entered yet — fades on hover to let user interact with table */}
              {localRows.length > 0 && localRows.every(r => r.dryTemperature === null && r.wetTemperature === null && r.coldWaterTemperature === null) && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => importFileRef.current?.click()}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') importFileRef.current?.click(); }}
                  style={{
                    position: 'absolute', inset: 0, zIndex: 2,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: '12px', cursor: 'pointer', userSelect: 'none', outline: 'none',
                    border: '2px dashed #3B82F6',
                    borderRadius: 'var(--nj-core-border-radius-200)',
                    background: 'rgba(255,255,255,0.88)',
                    opacity: isTableHovered ? 0 : 1,
                    pointerEvents: isTableHovered ? 'none' : 'auto',
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  <span className="material-icons" style={{ fontSize: '56px', color: '#3B82F6' }}>upload_file</span>
                  <NJText scale="md" style={{ margin: 0, textAlign: 'center', fontWeight: 700, color: '#3B82F6', fontFamily: "'Lato', sans-serif" }}>
                    Glissez-déposez le fichier des températures ici
                  </NJText>
                  <NJText scale="sm" style={{ margin: 0, color: 'var(--nj-semantic-color-text-secondary-default)', textAlign: 'center', fontFamily: "'Lato', sans-serif" }}>
                    ou cliquez sur la première case vide et collez<br />(Ctrl+V) vos données depuis Excel.
                  </NJText>
                </div>
              )}
              <DataTable<DemandHourlyRow>
                testId="trajectory-demand-table"
                data={localRows}
                verticalScroll
                header={colDefs}
                defaultColDef={{ flex: 1, sortable: false, filter: true, resizable: true, minWidth: 120 }}
                onGridReady={handleGridReady}
                onCellValueChanged={handleCellValueChanged}
              />
            </div>
          </div>
        ) : (
          <div ref={chartContainerRef} style={{ flex: 1, minHeight: 0 }}>
            <MixedChart type="mixed" data={chartData} options={chartOptions} heightPx={chartHeight} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab 3: Stockages ─────────────────────────────────────────────────────────

function StoragesTab() {
  const { data, isLoading, isError } = useStorages();

  const networkColDefs: ColDef<NetworkStorageRow>[] = [
    { field: 'name', headerName: 'Nom du stockage', flex: 2, editable: false },
    { field: 'initialLevel', headerName: 'Niveau initial (0-1)', flex: 1, editable: true },
    { field: 'finalLevel', headerName: 'Niveau final (0-1)', flex: 1, editable: true },
    { field: 'initialSupplyTemperature', headerName: '* Température aller initiale (°C)', flex: 1.5, editable: true },
    { field: 'initialReturnTemperature', headerName: '* Température retour initiale (°C)', flex: 1.5, editable: true },
  ];

  const iceColDefs: ColDef<IceStorageRow>[] = [
    { field: 'name', headerName: 'Nom du stockage', flex: 2, editable: false },
    { field: 'initialEnergyLevel', headerName: "Niveau inital d'énergie stockée (0-1)", flex: 1.5, editable: true },
    { field: 'finalEnergyLevel', headerName: "Niveau final d'énergie stockée (0-1)", flex: 1.5, editable: true },
  ];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--nj-core-space-800)' }}>
        <NJSpinner />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div style={{ padding: 'var(--nj-core-space-800)' }}>
        <NJInlineMessage variant="error" title="Impossible de charger les stockages." />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SP24, padding: SP24 }}>
      <div>
        <NJText style={{ fontWeight: 700, margin: `0 0 ${SP16} 0`, fontFamily: "'Lato', sans-serif" }}>
          Stockage du réseau
        </NJText>
        <DataTable<NetworkStorageRow>
          testId="trajectory-network-storages-table"
          data={data.networkStorages}
          header={networkColDefs}
          defaultColDef={{ sortable: false, filter: false, resizable: true }}
        />
      </div>

      <div>
        <NJText style={{ fontWeight: 700, margin: `0 0 ${SP16} 0`, fontFamily: "'Lato', sans-serif" }}>
          Stockage de glace
        </NJText>
        <DataTable<IceStorageRow>
          testId="trajectory-ice-storages-table"
          data={data.iceStorages}
          header={iceColDefs}
          defaultColDef={{ sortable: false, filter: false, resizable: true }}
        />
      </div>
    </div>
  );
}

// ─── Tab 4: Planning ──────────────────────────────────────────────────────────

function PlanningTab() {
  const { data, isLoading, isError } = usePlannings();
  const [selectedPlannings, setSelectedPlannings] = useState<Set<string>>(new Set());

  const togglePlanning = (id: string) => {
    setSelectedPlannings((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const plannings     = data ?? { production: [], topology: [] };
  const selectedNames = [...selectedPlannings].map((id) => {
    const all = [...plannings.production, ...plannings.topology];
    return all.find((p) => p.id === id)?.label ?? id;
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <NJSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <NJInlineMessage variant="error" title="Erreur de chargement">
        Impossible de charger les plannings.
      </NJInlineMessage>
    );
  }

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '280px 1fr', gap: SP24,
      minHeight: '420px', padding: SP24,
    }}>
      {/* Sidebar */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--nj-core-space-500)',
        paddingRight: SP24, borderRight: '1px solid var(--nj-semantic-color-border-secondary-default)',
      }}>
        <div>
          <p style={{
            margin: `0 0 ${SP8} 0`, fontSize: '12px',
            fontWeight: 'var(--nj-core-font-weight-semibold)',
            color: 'var(--nj-semantic-color-text-secondary-default)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>Planning de production</p>
          {plannings.production.length === 0 ? (
            <NJText scale="sm" style={{ color: 'var(--nj-semantic-color-text-secondary-default)' }}>
              Il n&apos;y a pas de planning de production disponible pour cette optimisation
            </NJText>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {plannings.production.map((p) => (
                <NJCheckbox key={p.id} label={p.label} isChecked={selectedPlannings.has(p.id)} onChange={() => togglePlanning(p.id)} />
              ))}
            </div>
          )}
        </div>
        <div>
          <p style={{
            margin: `0 0 ${SP8} 0`, fontSize: '12px',
            fontWeight: 'var(--nj-core-font-weight-semibold)',
            color: 'var(--nj-semantic-color-text-secondary-default)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>Planning de topologie</p>
          {plannings.topology.length === 0 ? (
            <NJText scale="sm" style={{ color: 'var(--nj-semantic-color-text-secondary-default)' }}>
              Il n&apos;y a pas de planning de topologie disponible pour cette optimisation
            </NJText>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {plannings.topology.map((p) => (
                <NJCheckbox key={p.id} label={p.label} isChecked={selectedPlannings.has(p.id)} onChange={() => togglePlanning(p.id)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div>
        {selectedPlannings.size === 0 ? (
          <NJInlineMessage variant="information" title="Veuillez sélectionner un planning">
            Sélectionnez un ou plusieurs plannings dans la liste de gauche.
          </NJInlineMessage>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SP16 }}>
            <NJText as="p" scale="sm" style={{ margin: 0 }}>
              <strong>{selectedPlannings.size}</strong>{' '}
              planning{selectedPlannings.size > 1 ? 's' : ''} sélectionné{selectedPlannings.size > 1 ? 's' : ''}{' '}
              : {selectedNames.join(', ')}
            </NJText>
            <div style={{
              border: '1px solid var(--nj-semantic-color-border-secondary-default)',
              borderRadius: 'var(--nj-core-border-radius-200)',
              padding: SP16, background: '#F6F8F9', overflowX: 'auto',
            }}>
              <div style={{
                display: 'flex', gap: SP16, marginBottom: SP16, paddingBottom: '8px',
                borderBottom: '1px solid var(--nj-semantic-color-border-secondary-default)',
              }}>
                <NJText scale="xs" style={{ margin: 0, color: 'var(--nj-semantic-color-text-secondary-default)', width: '160px', flexShrink: 0 }}>Actif de production</NJText>
                {[2020, 2025, 2030, 2035, 2040, 2045].map((y) => (
                  <NJText key={y} scale="xs" style={{ margin: 0, flex: 1, color: 'var(--nj-semantic-color-text-secondary-default)' }}>{y}</NJText>
                ))}
              </div>
              {['Groupe froid centrifuge', 'Groupe froid à absorption', 'PAC eau/eau', 'Tour de refroidissement', 'Secours groupes froids'].map((asset) => (
                <div key={asset} style={{
                  display: 'flex', gap: SP16, paddingBlock: '6px',
                  borderBottom: '1px solid var(--nj-semantic-color-border-secondary-default)',
                }}>
                  <NJText scale="xs" style={{ margin: 0, width: '160px', flexShrink: 0 }}>{asset}</NJText>
                  {[2020, 2025, 2030, 2035, 2040, 2045].map((y) => (
                    <div key={y} style={{ flex: 1 }} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab 6: Options avancées ──────────────────────────────────────────────────

type OptionEntry = { key: keyof AdvancedOptions; label: string; section: string };

const LEFT_OPTIONS: OptionEntry[] = [
  { key: 'simplified_pressure_loss_model', label: 'Utilisation du modèle simplifié de calcul des pertes de charge', section: 'Dimensionnement' },
  { key: 'delta_t_min',                    label: 'Prendre en compte un seuil de delta T minimal sur les Sous-stations', section: 'Sous-Stations' },
  { key: 'ets_flow_correction',            label: 'Correction des débits en sous-stations',                         section: 'Sous-Stations' },
  { key: 'multiple_prod_temperatures',     label: 'Températures multiples de la ligne de production',               section: 'Groupes Froid' },
  { key: 'handle_power_gaps',              label: 'Traiter les trous de puissance',                                  section: 'Groupes Froid' },
  { key: 'gf_switch_penalty',              label: 'Pénalisation des switches GF',                                   section: 'Groupes Froid' },
  { key: 'rolling_anticipative',           label: 'Rolling anticipatif',                                            section: 'Groupes Froid' },
  { key: 'unit_commitment_short_term',     label: 'Engagement des unités par temps de fonctionnement (court-terme)', section: 'Groupes Froid' },
  { key: 'aggregate_lt_timesteps',         label: "Agrégation des pas de temps sur les optimisations long-terme",   section: 'Groupes Froid' },
  { key: 'cold_water_aggregation_level',   label: "Niveau d'agrégation pour les sites de production d'eau froide",  section: 'Groupes Froid' },
  { key: 'aggregate_non_detailed_timesteps', label: 'Agréger les pas de temps non détaillés',                       section: 'Groupes Froid' },
  { key: 'use_generic_tar_model',          label: 'Utiliser le modèle TAR générique',                               section: 'Systèmes De Refroidissement' },
  { key: 'endogenous_recirculation',       label: 'Recirculation endogène',                                         section: 'Systèmes De Refroidissement' },
  { key: 'variable_exchanger_delta_p',     label: 'Prendre en compte la delta_P variable des échangeurs',           section: 'Systèmes De Refroidissement' },
  { key: 'bypass_activation',              label: 'Activation du bypass de la centrale (surdébit en production)',   section: 'Systèmes De Refroidissement' },
];

const RIGHT_OPTIONS: OptionEntry[] = [
  { key: 'convex_hull_pumps',              label: 'Convex hull pour les pompes',                                    section: 'Pompes' },
  { key: 'evaporator_pumps',               label: 'Prendre en compte les pompes évaporateur',                       section: 'Pompes' },
  { key: 'condenser_pumps',                label: 'Prendre en compte les pompes condenseur',                        section: 'Pompes' },
  { key: 'generate_gf_detailed_files',     label: 'Génération des fichiers détaillés sur les GF',                   section: 'Résultats' },
  { key: 'generate_limiting_factors_report', label: 'Générer le fichier des facteurs limitants',                    section: 'Résultats' },
  { key: 'short',                          label: 'Short',                                                          section: 'Autre' },
  { key: 'cop_optimization',               label: 'Optimisation du COP réseau',                                     section: 'Autre' },
  { key: 'flow_replay_prod_sites',         label: 'Rejeux en débit des sites de production',                        section: 'Autre' },
  { key: 'short_term_constraints_target',  label: 'Prise en compte des contraintes court-terme dans le tir target', section: 'Autre' },
  { key: 'test_feasopt',                   label: 'Activer FeasOpt',                                                section: 'Débogage' },
  { key: 'relax_regulators',               label: 'Relaxation variables binaires régulateurs',                      section: 'Débogage' },
  { key: 'writelp',                        label: 'Writelp',                                                        section: 'Débogage' },
  { key: 'debug_generate_logs',            label: 'Activer mode debug',                                             section: 'Débogage' },
  { key: 'data_check',                     label: 'Data check',                                                     section: 'Débogage' },
  { key: 'iis',                            label: 'Générer IIS si infaisable',                                      section: 'Débogage' },
  { key: 'debug_target_gdx',               label: 'Debug target gdx',                                               section: 'Débogage' },
  { key: 'limit_resolution_time',          label: 'Limiter temps de résolution',                                    section: 'Débogage' },
];

function renderOptionSections(
  entries: OptionEntry[],
  values: AdvancedOptions,
  onToggle: (key: keyof AdvancedOptions, value: boolean) => void,
) {
  const sections = [...new Set(entries.map((o) => o.section))];
  return sections.map((section) => (
    <ToggleSection key={section} title={section}>
      {entries.filter((o) => o.section === section).map((o) => (
        <ToggleRow key={o.key} label={o.label} checked={values[o.key]} onChange={(v) => onToggle(o.key, v)} />
      ))}
    </ToggleSection>
  ));
}

interface AdvancedOptionsTabProps {
  options: AdvancedOptions;
  onToggle: (key: keyof AdvancedOptions, value: boolean) => void;
}
function AdvancedOptionsTab({ options, onToggle }: AdvancedOptionsTabProps) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 1fr',
      gap: 'var(--nj-core-space-800)', alignItems: 'start', padding: SP24,
    }}>
      <div>{renderOptionSections(LEFT_OPTIONS, options, onToggle)}</div>
      <div>{renderOptionSections(RIGHT_OPTIONS, options, onToggle)}</div>
    </div>
  );
}

// ─── Tab 5: Autre ─────────────────────────────────────────────────────────────

// Mock topology — in production these come from network topology & production data API
const REPLAY_SHEETS: { name: string; entities: string[]; unit: string }[] = [
  { name: 'ProductionUnit.mass_flow', entities: ['CBER_Chiller_Default', 'CBNF_Chiller_Default'], unit: 'kg/s' },
  { name: 'ProductionUnit.power',     entities: ['Return-BERCY', 'Return-BNF'],                   unit: 'W'    },
  { name: 'ProductionSite.delta_t',   entities: ['CBER_Chiller_Default', 'CBNF_Chiller_Default'], unit: 'K'    },
  { name: 'ProductionSite.delta_p',   entities: ['CBER', 'CBNF'],                                 unit: 'Pa'   },
  { name: 'Node.pressure',            entities: ['identifier_node'],                              unit: 'Pa'   },
  { name: 'Departure.pressure',       entities: ['Departure-BERCY', 'Departure-BNF'],             unit: 'Pa'   },
  { name: 'Departure.mass_flow',      entities: ['Departure-BERCY', 'Departure-BNF'],             unit: 'kg/s' },
  { name: 'Departure.temperature',    entities: ['Departure-BERCY', 'Departure-BNF'],             unit: 'K'    },
  { name: 'Return.temperature',       entities: ['Departure-BERCY', 'Departure-BNF'],             unit: 'K'    },
  { name: 'Return.pressure',          entities: ['Return-BERCY', 'Return-BNF'],                   unit: 'Pa'   },
];

function generateReplayTemplate(startDate: string, nbSteps: number): void {
  const wb = XLSX.utils.book_new();

  const pad = (n: number) => String(n).padStart(2, '0');

  for (const { name, entities, unit } of REPLAY_SHEETS) {
    const entityRow: (string)[] = ['', '', ...entities.flatMap(e => [e, ''])];
    const headerRow: string[] = ['Datetime', 'utc_offset', ...entities.flatMap(() => [`min [${unit}]`, `max [${unit}]`])];

    const dataRows = Array.from({ length: nbSteps }, (_, i) => {
      const d = new Date(startDate);
      d.setHours(d.getHours() + i);
      const localStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      const offsetMin = -d.getTimezoneOffset();
      const sign = offsetMin >= 0 ? '+' : '-';
      const absMin = Math.abs(offsetMin);
      const utcOffset = `${sign}${pad(Math.floor(absMin / 60))}:${pad(absMin % 60)}`;
      return [localStr, utcOffset];
    });

    const ws = XLSX.utils.aoa_to_sheet([entityRow, headerRow, ...dataRows]);

    // Merge entity header cells: 2 columns per entity starting at col 2
    ws['!merges'] = entities.map((_, i) => ({
      s: { r: 0, c: 2 + i * 2 },
      e: { r: 0, c: 3 + i * 2 },
    }));

    XLSX.utils.book_append_sheet(wb, ws, name);
  }

  XLSX.writeFile(wb, 'Template_replay.xlsx');
}

function AutreTab({ startDate, nbSteps }: { startDate: string; nbSteps: number }) {
  return (
    <div style={{ padding: SP24, maxWidth: '560px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        fontFamily: "'Lato', sans-serif",
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: '20px',
        lineHeight: '28px',
        color: '#000000',
        height: '31px',
        flex: 'none',
        flexGrow: 0,
        margin: `0 0 ${SP24} 0`,
      }}>
        Configuration avancée
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SP16 }}>
        <NJText scale="md" style={{ margin: 0, fontWeight: 700, fontFamily: "'Lato', sans-serif" }}>
          Rejeux
        </NJText>

        <FileUploadCard
          accept=".xlsx,.xls"
          acceptLabel="Excel (.xlsx, .xls)"
          maxSizeMb={10}
        />

        {/* Links row — order: 3 per Figma spec */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '8px 0px 0px',
          gap: '8px',
          width: '347px',
          height: '24px',
          flex: 'none',
          flexGrow: 0,
        }}>
          <a
            href="#"
            onClick={e => {
              e.preventDefault();
              if (startDate && nbSteps > 0) generateReplayTemplate(startDate, nbSteps);
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              fontFamily: "'Lato', sans-serif", fontSize: '14px', fontWeight: 500,
              color: startDate && nbSteps > 0
                ? 'var(--nj-semantic-color-brand-primary, #0078D4)'
                : 'var(--nj-semantic-color-text-neutral-tertiary-default, #60798B)',
              textDecoration: 'none',
              cursor: startDate && nbSteps > 0 ? 'pointer' : 'default',
            }}
          >
            <span className="material-icons-outlined" style={{ fontSize: '16px' }}>download</span>
            Télécharger le template
          </a>
          <span style={{ color: '#9EADB8', fontSize: '14px' }}>•</span>
          <a
            href="https://confluence.tools.digital.engie.com/spaces/EDNC/pages/529041893/Replay+Optimisation"
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              fontFamily: "'Lato', sans-serif", fontSize: '14px', fontWeight: 500,
              color: 'var(--nj-semantic-color-brand-primary, #0078D4)',
              textDecoration: 'none',
            }}
          >
            <span className="material-icons-outlined" style={{ fontSize: '16px' }}>menu_book</span>
            Guide utilisateur
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface ShortTermFormProps {
  initialValues?: { name?: string; description?: string; startDate?: string; steps?: number; regulationMode?: string; };
  onClose?: () => void;
  embedded?: boolean;
}

export default function NewOptiFormShortTerm({ initialValues, onClose, embedded }: ShortTermFormProps = {}) {
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [nom, setNom]             = useState(initialValues?.name ?? '');
  const [description, setDescription] = useState(initialValues?.description ?? '');
  const [startDate, setStartDate] = useState(initialValues?.startDate ?? '');
  const [nombreDePas, setNombreDePas] = useState(initialValues?.steps ? String(initialValues.steps) : '');
  const [endDate, setEndDate]     = useState('');
  const [regulationMode, setRegulationMode] = useState<RegulationMode>((initialValues?.regulationMode as RegulationMode) ?? (DELTA_TEMPERATURE as RegulationMode));
  const [advancedOptions, setAdvancedOptions] = useState<AdvancedOptions>(DEFAULT_ADVANCED_OPTIONS);
  const [isProductionDataValid, setIsProductionDataValid] = useState(true);

  const nbStepsParsed = Math.max(0, parseInt(nombreDePas, 10) || 0);

  // Auto-compute endDate when startDate or nombreDePas change
  useEffect(() => {
    if (!startDate || nbStepsParsed <= 0) return;
    setEndDate(computeEndDate(startDate, nbStepsParsed));
  }, [startDate, nbStepsParsed]);

  // Auto-compute nombreDePas when endDate is edited directly
  const handleEndDateChange = (v: string) => {
    setEndDate(v);
    if (startDate && v) {
      const steps = computeNbSteps(startDate, v);
      if (steps > 0) setNombreDePas(String(steps));
    }
  };

  const activeTabIndex = TABS.findIndex((t) => t.id === activeTab);
  const isFirstTab     = activeTabIndex === 0;
  const isLastTab      = activeTabIndex === TABS.length - 1;

  const [visitedTabs, setVisitedTabs] = useState<Set<TabId>>(new Set(['general']));
  useEffect(() => {
    setVisitedTabs((prev) => (prev.has(activeTab) ? prev : new Set([...prev, activeTab])));
  }, [activeTab]);

  const goToPrev = () => { if (activeTabIndex > 0) setActiveTab(TABS[activeTabIndex - 1].id); };
  const goToNext = () => { if (activeTabIndex < TABS.length - 1) setActiveTab(TABS[activeTabIndex + 1].id); };
  const isSmall  = useIsSmallScreen();

  const toggleAdvancedOption = (key: keyof AdvancedOptions, value: boolean) => {
    setAdvancedOptions((prev) => ({ ...prev, [key]: value }));
  };

  const innerWrapperStyle: CSSProperties = embedded
    ? { maxWidth: '100%', margin: 0, height: '100%' }
    : { maxWidth: '1400px', margin: '0 auto' };

  const formCardStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: embedded ? '100%' : 'calc(100vh - 80px)',
    minHeight: embedded ? 0 : '640px',
    background: 'var(--nj-semantic-color-background-primary-default)',
    border: '1px solid var(--nj-semantic-color-border-secondary-default)',
    borderRadius: 'var(--nj-core-border-radius-300)',
    boxShadow: 'var(--nj-core-shadow-200)',
    overflow: 'hidden',
  };

  const inner = (
    <div style={innerWrapperStyle}>
        <div style={formCardStyle}>

          {/* ── Header ── */}
          <div style={{
            display: 'flex', flexDirection: 'row', alignItems: 'center',
            justifyContent: 'space-between', gap: '48px',
            padding: '16px 24px', height: '64px', flexShrink: 0,
            background: '#F6F8F9', borderBottom: '1px solid #CCD4DA', boxSizing: 'border-box',
          }}>
            <span style={{
              fontFamily: "'Lato', sans-serif", fontWeight: 700, fontSize: '16px',
              lineHeight: '24px', color: 'var(--nj-semantic-color-text-primary-default)', flexShrink: 0,
            }}>
              Formulaire de création d&apos;une optimisation courte terme
            </span>
            <NJIconButton icon="close" label="Fermer" variant="tertiary" scale="sm" onClick={onClose} />
          </div>

          {/* ── Tabs ── */}
          <div style={{
            display: 'flex', flexDirection: 'row', alignItems: 'center',
            padding: '0px 24px', gap: '16px', flexShrink: 0,
            background: '#ffffff', boxSizing: 'border-box',
          }}>
            <NJTabs
              label="Étapes du formulaire"
              activeTab={activeTab}
              onClickTabItem={(id: string) => setActiveTab(id as TabId)}
            >
              {TABS.map((tab) => (
                <NJTab
                  key={tab.id}
                  id={tab.id}
                  label={isSmall ? tab.shortLabel : tab.label}
                  ariaControls={`panel-${tab.id}`}
                  isActive={activeTab === tab.id}
                />
              ))}
            </NJTabs>
          </div>

          {/* ── Tab panels ── */}
          <div style={{
            flex: 1, minHeight: 0,
            overflow: activeTab === 'temperatures-demand' ? 'hidden' : 'auto',
            display: 'flex', flexDirection: 'column', background: '#ffffff',
          }}>
            <NJTabPanel id="panel-general" isActive={activeTab === 'general'}>
              <GeneralTab
                nom={nom}                           onNomChange={setNom}
                description={description}           onDescriptionChange={setDescription}
                startDate={startDate}               onStartDateChange={setStartDate}
                nombreDePas={nombreDePas}           onNombreDePasChange={setNombreDePas}
                endDate={endDate}                   onEndDateChange={handleEndDateChange}
                regulationMode={regulationMode}     onRegulationModeChange={setRegulationMode}
                advancedOptions={advancedOptions}
                onToggleOption={toggleAdvancedOption}
              />
            </NJTabPanel>

            <NJTabPanel id="panel-temperatures-demand" isActive={activeTab === 'temperatures-demand'}>
              {visitedTabs.has('temperatures-demand') && (
                <TempDemandTab
                  onProductionValidityChange={setIsProductionDataValid}
                  startDate={startDate}
                  nbSteps={nbStepsParsed}
                />
              )}
            </NJTabPanel>

            <NJTabPanel id="panel-storages" isActive={activeTab === 'storages'}>
              {visitedTabs.has('storages') && <StoragesTab />}
            </NJTabPanel>

            <NJTabPanel id="panel-planning" isActive={activeTab === 'planning'}>
              {visitedTabs.has('planning') && <PlanningTab />}
            </NJTabPanel>

            <NJTabPanel id="panel-other" isActive={activeTab === 'other'}>
              <AutreTab startDate={startDate} nbSteps={nbStepsParsed} />
            </NJTabPanel>

            <NJTabPanel id="panel-advanced" isActive={activeTab === 'advanced'}>
              {visitedTabs.has('advanced') && <AdvancedOptionsTab options={advancedOptions} onToggle={toggleAdvancedOption} />}
            </NJTabPanel>
          </div>

          {/* ── Footer ── */}
          <div style={{
            display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center',
            padding: '16px 24px', gap: '16px', height: '56px', flexShrink: 0,
            borderTop: '1px solid #CCD4DA', background: '#ffffff', boxSizing: 'border-box',
          }}>
            <NJButton label="Annuler" variant="secondary" scale={isSmall ? 'sm' : 'md'} onClick={onClose} />
            <NJButton
              label={isSmall ? 'Préc.' : 'Précédent'}
              emphasis="subtle"
              scale={isSmall ? 'sm' : 'md'}
              disabled={isFirstTab}
              onClick={goToPrev}
            />
            <NJButton
              label="Suivant"
              emphasis="subtle"
              scale={isSmall ? 'sm' : 'md'}
              disabled={isLastTab}
              onClick={goToNext}
            />
            <NJButton
              label={isSmall ? 'Lancer' : "Lancer l'optimisation"}
              scale={isSmall ? 'sm' : 'md'}
              disabled={!nom.trim() || !isProductionDataValid}
              onClick={() => undefined}
            />
          </div>
        </div>
      </div>
  );
  if (embedded) return inner;
  return (
    <PrototypeStudioLayout title="Formulaire de création d'une optimisation courte terme">
      {inner}
    </PrototypeStudioLayout>
  );
}
